import groupBy from "lodash/groupBy";
import useEnabledConfigurableFields from "./useEnabledConfigurableFields";
import useEnabledStorage from "./useEnabledStorage";
import useEnabledDescriptive from "./useEnabledDescriptive";
import useEnabledIdentifiers from "./useEnabledIdentifiers";
import useEnabledOrigin from "./useEnabledOrigin";
import useEnabledDestination from "./useEnabledDestination";
import useAllFields from "../../../hooks/useAllFields";
import useAuth from "../../../hooks/useAuth";
import { applyReceivingWizardFieldVisibility } from "../../../constants/fields";
import { isOnsiteCaptureProject } from "../../../components/modals/ProjectActions/projectReceivingWorkflow";

const enabledLegacySectionFields = (fields, display, section) =>
  fields
    .filter(([id]) => display[id])
    .map(([id, _category, label]) => ({ id, label, section }));

const processSavedState = (parsedState, enabledFields) => {
  const fieldConfig = {};
  Object.entries(parsedState.sections).forEach(([sectionId, fieldIds]) => {
    fieldIds.forEach((id, index) => {
      fieldConfig[id] = { section: sectionId, index };
    });
  });

  const processedFields = enabledFields.map(field => {
    const config = fieldConfig[field.id];
    if (config) {
      return {
        ...field,
        section: config.section,
        _orderIndex: config.index,
      };
    }
    return {
      ...field,
      _orderIndex: 9999,
    };
  });

  processedFields.sort((a, b) => a._orderIndex - b._orderIndex);

  return groupBy(processedFields, "section");
};

export default function useEnabledFields({
  display,
  type,
  asset,
  onRestockInventory,
  availableQuantityComponent,
  sameLocation,
  manufacturersOptions,
  colorsOptions,
  dispositionOptions,
  handleUpdateAssetField,
  project,
  disabledForm,
  isReceivingWizardDisabled,
}) {
  const { user } = useAuth();
  const visibleDisplay = applyReceivingWizardFieldVisibility(display, {
    ...user.company.settings,
    onsiteCaptureMode: isOnsiteCaptureProject(project),
  });
  const configurableFields = useEnabledConfigurableFields(visibleDisplay, {
    assetCustomFields: disabledForm ? asset?.customFields : undefined,
  });
  const { electricalInfo, plumbingInfo, mechanicalInfo } = useAllFields(project);
  const identifiers = useEnabledIdentifiers({
    display: visibleDisplay,
    disabledForm,
    asset,
    type,
    project,
    isReceivingWizardDisabled,
  });
  const storageFields = useEnabledStorage({
    onRestockInventory,
    display: visibleDisplay,
    type,
    asset,
    availableQuantityComponent,
    sameLocation,
    project,
  });
  const descriptiveFields = useEnabledDescriptive({
    display: visibleDisplay,
    manufacturersOptions,
    colorsOptions,
    dispositionOptions,
    type,
    asset,
    handleUpdateAssetField,
  });
  const originFields = useEnabledOrigin({
    display: visibleDisplay,
    disabledForm,
    asset,
  });
  const destinationFields = useEnabledDestination({
    display: visibleDisplay,
    disabledForm,
    type,
    asset,
  });
  const electricalFields = enabledLegacySectionFields(electricalInfo, visibleDisplay, "electrical");
  const plumbingFields = enabledLegacySectionFields(plumbingInfo, visibleDisplay, "plumbing");
  const mechanicalFields = enabledLegacySectionFields(mechanicalInfo, visibleDisplay, "mechanical");
  const enabledFields = [
    ...identifiers,
    ...configurableFields,
    ...storageFields,
    ...descriptiveFields,
    ...originFields,
    ...destinationFields,
    ...electricalFields,
    ...plumbingFields,
    ...mechanicalFields,
  ];

  const enabledSections = Array.from(new Set(enabledFields.map(field => field.section)));

  const fieldOrder = project?.fieldSettings?.fieldOrder;
  if (fieldOrder?.version === 1 && fieldOrder?.sections) {
    return {
      enabledSections,
      orderedSection: processSavedState(fieldOrder, enabledFields),
    };
  }

  return {
    enabledSections,
    orderedSection: groupBy(enabledFields, "section"),
  };
}
